import React, { useEffect, useRef } from "react";
import { drawWallHackWalls, WALLHACK_VARIANTS, WallGeo } from "../../game-canvas/wallhack-designs";

// Dev-only gallery to compare wall-hack WALL effects (drawn on the arena border instead
// of as a ring on the worm). Each tile shows the design on a rectangle arena and a circle
// arena, animating on a loop. Route: /wh
const COLOR = "#22c55e"; // wall-hack self colour (green)
const TILE_W = 340;
const TILE_H = 180;
const BW = 12; // wall thickness in px (a touch chunkier than in-game so it reads here)

// A rectangle arena on the left, a circle arena on the right, both with a wall band.
const RECT: WallGeo = { type: "rectangle", x: 24, y: 34, w: 120, h: 112, bw: BW };
const CIRC: WallGeo = { type: "circle", cx: 250, cy: 90, radius: 58, bw: BW };
const MAP_BG = "#0d1320";
const MAP_BORDER = "#2b3b56";

function drawArena(ctx: CanvasRenderingContext2D, g: WallGeo) {
  // base wall band (grey, like the real border) + dark play area
  ctx.fillStyle = MAP_BORDER;
  if (g.type === "rectangle") { ctx.fillRect(g.x - g.bw, g.y - g.bw, g.w + 2 * g.bw, g.h + 2 * g.bw); ctx.fillStyle = MAP_BG; ctx.fillRect(g.x, g.y, g.w, g.h); }
  else { ctx.beginPath(); ctx.arc(g.cx, g.cy, g.radius + g.bw, 0, Math.PI * 2); ctx.fill(); ctx.fillStyle = MAP_BG; ctx.beginPath(); ctx.arc(g.cx, g.cy, g.radius, 0, Math.PI * 2); ctx.fill(); }
}

export default function WallHackDesignsDemo() {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvasRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });

    function loop(now: number) {
      if (!startRef.current) startRef.current = now;
      const t = (now - startRef.current) / 1000;
      const frac = 1 - ((t % 6) / 6); // loops 1 -> 0 so countdown designs (32) drain visibly
      WALLHACK_VARIANTS.forEach((v, idx) => {
        const c = canvasRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        [RECT, CIRC].forEach((g) => {
          drawArena(ctx, g);
          drawWallHackWalls(ctx, g, v.n, t, COLOR, frac);
        });
      });
      rafRef.current = window.requestAnimationFrame(loop);
    }
    rafRef.current = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{ maxWidth: 1120, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Wall-hack wall effects</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        When wall-hack is active the effect lives on the arena walls, not as a ring on the worm.
        Each tile shows a design on a square arena and a circle arena. Switch it live in a match with{" "}
        <b>?wh=1..52</b>. Countdown designs (32+) drain with a looping timer here.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 14, marginTop: 16 }}>
        {WALLHACK_VARIANTS.map((v, idx) => (
          <div key={v.n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "8px 11px" }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{v.n}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#c3ccdf" }}>{v.label}</span>
            </div>
            <canvas ref={(el) => { canvasRefs.current[idx] = el; }} style={{ width: TILE_W, height: TILE_H, display: "block", background: "#070a10" }} />
          </div>
        ))}
      </div>
    </div>
  );
}
